/**
 * Feedback Provider Component
 * Manages feedback modal state and provides context for feedback functionality
 */

import React, { Suspense, useCallback, useContext, useState } from 'react';

import { EMPTY_GUID } from '../../../../constants';
import { OcChatConfig } from '../../../../types/oc';

interface FeedbackContextValue {
  isOpen: boolean;
  toggleModal: () => void;
  openFeedbackModal: (messageKey: string) => void;
  messageKey: string;
  config?: OcChatConfig;
}

const FeedbackContext = React.createContext<FeedbackContextValue | null>(null);
FeedbackContext.displayName = 'FeedbackContext';

/**
 * Hook to access feedback context
 * Must be used within FeedbackProvider
 */
export const useFeedbackContext = (): FeedbackContextValue => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedbackContext must be used within FeedbackProvider');
  }
  return context;
};

// Lazy load the modal to reduce initial bundle size
const ShareFeedbackModal = React.lazy(
  () => import('./ShareFeedbackModal'),
);

export interface FeedbackProviderProps {
  children: React.ReactNode;
  config?: OcChatConfig;
}

/**
 * FeedbackProvider Component
 * Provides feedback modal state management and lazy loads the modal
 */
export const FeedbackProvider: React.FC<FeedbackProviderProps> = ({
  children,
  config,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackMessageKey, setFeedbackMessageKey] = useState(EMPTY_GUID);

  const toggleModal = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const openFeedbackModal = useCallback((messageKey: string) => {
    setFeedbackMessageKey(messageKey);
    setIsOpen(true);
  }, []);

  return (
    <FeedbackContext.Provider
      value={{ isOpen, toggleModal, openFeedbackModal, messageKey: feedbackMessageKey, config }}
    >
      <>
        <Suspense fallback={null}>
          {isOpen && <ShareFeedbackModal />}
        </Suspense>
        {children}
      </>
    </FeedbackContext.Provider>
  );
};

export default FeedbackProvider;

